/**
 * SPRINT-035: Hosted Checkout API client.
 *
 * All calls use `Authorization: CheckoutSession {fullSecret}` where fullSecret
 * is the full string as issued: "{sessionId}.{rawSecret}".
 *
 * Response shapes confirmed from backend source:
 * - GET  /sessions/:id             → CheckoutSession (direct)
 * - GET  /sessions/:id/payment-options → { options: PaymentOption[] }
 * - POST /sessions/:id/attempts    → AttemptResult (direct)
 * - POST /sessions/:id/cancel      → { success: boolean, sessionId: string }
 */

import type { CheckoutSession, AttemptResult, PaymentOption, SavedMethodOneTap, CheckoutError } from './types';

const API_BASE = import.meta.env.VITE_API_URL ?? window.location.origin;

export class CheckoutAPI {
  constructor(private readonly clientSecret: string) {}

  private headers(idempotencyKey?: string): HeadersInit {
    // SECURITY: Pass the FULL secret exactly as issued — no recomposition
    const h: Record<string, string> = {
      Authorization: `CheckoutSession ${this.clientSecret}`,
      'Content-Type': 'application/json',
    };
    if (idempotencyKey) {
      h['Idempotency-Key'] = idempotencyKey;
    }
    return h;
  }

  async fetchSession(sessionId: string): Promise<CheckoutSession> {
    const res = await fetch(
      `${API_BASE}/v4/checkout/sessions/${sessionId}`,
      { headers: this.headers() },
    );

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw this.toError(res.status, body);
    }

    return res.json() as Promise<CheckoutSession>;
  }

  async fetchPaymentOptions(sessionId: string): Promise<{ options: PaymentOption[]; savedMethodOneTap: SavedMethodOneTap | null }> {
    const res = await fetch(
      `${API_BASE}/v4/checkout/sessions/${sessionId}/payment-options`,
      { headers: this.headers() },
    );

    if (!res.ok) {
      return { options: [], savedMethodOneTap: null };
    }

    const data = await res.json() as { options?: PaymentOption[]; savedMethodOneTap?: SavedMethodOneTap | null };
    return {
      options: data.options ?? [],
      savedMethodOneTap: data.savedMethodOneTap ?? null,
    };
  }

  async createAttempt(
    sessionId: string,
    methodKey: string,
    channelType: string,
    opts: {
      walletConfirm?: boolean;
      walletAccountId?: string;
      idempotencyKey?: string;
      metadata?: Record<string, unknown>;
      customer?: Record<string, unknown>;
      /** [PR-14.25] Tokenized payment method — payment_method_id only, never PAN/CVV */
      paymentMethod?: Record<string, unknown>;
    } = {},
  ): Promise<AttemptResult> {
    const body: Record<string, unknown> = {
      method_key: methodKey,
      channel_type: channelType,
      return_url: `${window.location.origin}/sessions/${sessionId}/return`,
    };

    if (methodKey === 'platform_wallet') {
      body.wallet = {
        confirm: opts.walletConfirm ?? true,
        ...(opts.walletAccountId ? { wallet_account_id: opts.walletAccountId } : {}),
      };
    }

    if (opts.metadata && Object.keys(opts.metadata).length > 0) {
      body.metadata = opts.metadata;
    }

    if (opts.customer && Object.keys(opts.customer).length > 0) {
      body.customer = opts.customer;
    }

    // [PR-14.25 / INVARIANT 2] payment_method carries token only — assertNoRawCardData guards backend
    if (opts.paymentMethod && Object.keys(opts.paymentMethod).length > 0) {
      body.payment_method = opts.paymentMethod;
    }

    // Use provided key or generate one — always include to protect against retries
    const idempotencyKey =
      opts.idempotencyKey ??
      `${sessionId}-${methodKey}-${Date.now()}`;

    const res = await fetch(
      `${API_BASE}/v4/checkout/sessions/${sessionId}/attempts`,
      {
        method: 'POST',
        headers: this.headers(idempotencyKey),
        body: JSON.stringify(body),
      },
    );

    if (!res.ok) {
      const body2 = await res.json().catch(() => ({}));
      throw this.toError(res.status, body2);
    }

    return res.json() as Promise<AttemptResult>;
  }

  async cancelSession(sessionId: string): Promise<void> {
    await fetch(`${API_BASE}/v4/checkout/sessions/${sessionId}/cancel`, {
      method: 'POST',
      headers: this.headers(),
    });
  }

  // -------------------------------------------------------------------------
  // Error mapping
  // -------------------------------------------------------------------------

  private toError(status: number, body: Record<string, unknown>): CheckoutError {
    const code = (body.error as string) ?? 'UNKNOWN_ERROR';
    const message = (body.message as string) ?? 'An unexpected error occurred.';

    if (status === 410) {
      return { code: 'SESSION_EXPIRED', message: 'This checkout session has expired.', recoverable: false };
    }
    if (status === 401) {
      return { code: 'UNAUTHORIZED', message: 'Invalid or missing authentication.', recoverable: false };
    }
    if (status === 409 && code === 'ATTEMPT_IN_PROGRESS') {
      return { code: 'ATTEMPT_IN_PROGRESS', message, recoverable: true };
    }
    if (status === 409) {
      return { code, message, recoverable: false };
    }
    if (status === 422) {
      return { code, message, recoverable: false };
    }
    if (status >= 500) {
      return { code: 'SERVER_ERROR', message: 'A server error occurred. Please try again.', recoverable: true };
    }
    return { code, message, recoverable: false };
  }
}
